import { z } from "zod";

export const PLATFORM_ACTION_KINDS = [
  "url",
  "webinar_registration",
  "affiliate_referral",
  "offer_checkout",
] as const;

export type PlatformActionKind = (typeof PLATFORM_ACTION_KINDS)[number];

export const platformUrlActionSchema = z.object({
  kind: z.literal("url"),
  href: z.string().min(1).max(2000),
  openInNewTab: z.boolean().optional(),
}).strict();

export const webinarRegistrationActionSchema = z.object({
  kind: z.literal("webinar_registration"),
  webinarId: z.string().uuid(),
  registrationPageUrl: z.string().min(1).max(2000).optional(),
  registrationMode: z.enum(["hosted_page", "mounted_api"]).optional().default("mounted_api"),
}).strict();

export const affiliateReferralActionSchema = z.object({
  kind: z.literal("affiliate_referral"),
  referralCode: z.string().min(1).max(120),
  campaignName: z.string().min(1).max(200).optional(),
  utmSource: z.string().min(1).max(100).optional().default("affiliate"),
  utmMedium: z.string().min(1).max(100).optional().default("referral"),
}).strict();

export const offerCheckoutActionSchema = z.object({
  kind: z.literal("offer_checkout"),
  offerId: z.string().min(1).max(120).optional(),
  planKey: z.string().min(1).max(120).optional(),
  checkoutUrl: z.string().min(1).max(2000).optional(),
  checkoutMode: z.enum(["hosted_url", "authenticated_api"]).optional().default("authenticated_api"),
}).strict();

export const platformActionTargetSchema = z.discriminatedUnion("kind", [
  platformUrlActionSchema,
  webinarRegistrationActionSchema,
  affiliateReferralActionSchema,
  offerCheckoutActionSchema,
]);

export type PlatformActionTarget = z.infer<typeof platformActionTargetSchema>;

export const ORDER_BUMP_BINDING_KINDS = [
  "funnel_submission",
] as const;

export type OrderBumpBindingKind = (typeof ORDER_BUMP_BINDING_KINDS)[number];

export const orderBumpBindingSchema = z.object({
  kind: z.literal("funnel_submission"),
  fieldName: z.string().regex(/^[A-Za-z0-9_-]+$/).optional(),
  selectedValue: z.string().min(1).max(200).optional().default("1"),
  offerId: z.string().min(1).max(120).optional(),
}).strict();

export type OrderBumpBinding = z.infer<typeof orderBumpBindingSchema>;

export const WEBINAR_PAGE_ROLES = [
  "registration",
  "evergreen_replay",
  "live_room",
  "post_webinar_offer",
] as const;

export type WebinarPageRole = (typeof WEBINAR_PAGE_ROLES)[number];

export const webinarPageConfigSchema = z.object({
  webinarId: z.string().uuid(),
  pageRole: z.enum(WEBINAR_PAGE_ROLES),
  registrationFormId: z.string().uuid().nullable().optional(),
  evergreenMode: z.enum([
    "scheduled_replay",
    "just_in_time",
    "on_demand",
    "automated_series",
  ]).optional(),
  scheduledAt: z.string().datetime().optional(),
  primaryAction: platformActionTargetSchema.optional(),
  affiliateAction: affiliateReferralActionSchema.optional(),
}).strict().superRefine((value, ctx) => {
  if (value.pageRole === "registration") {
    const actionKind = value.primaryAction?.kind;
    if (!value.registrationFormId && actionKind !== "webinar_registration") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Registration pages require registrationFormId or a webinar_registration primaryAction",
        path: ["registrationFormId"],
      });
    }

    if (value.primaryAction?.kind === "webinar_registration" && value.primaryAction.webinarId !== value.webinarId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "webinar_registration primaryAction must target the same webinarId as the page config",
        path: ["primaryAction"],
      });
    }
  }

  if (value.pageRole === "evergreen_replay" && !value.evergreenMode) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Evergreen replay pages require evergreenMode",
      path: ["evergreenMode"],
    });
  }

  if (value.pageRole === "live_room" && !value.scheduledAt) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Live-room pages require scheduledAt so countdown and attendance state stay explicit",
      path: ["scheduledAt"],
    });
  }
});

export type WebinarPageConfig = z.infer<typeof webinarPageConfigSchema>;

export interface ResolvedPlatformActionTarget {
  href: string | null;
  openInNewTab: boolean;
  isInteractive: boolean;
  reason: string | null;
  attrs: Record<string, string>;
}

function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function toQueryString(params: Record<string, string | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      query.set(key, value);
    }
  }

  const encoded = query.toString();
  return encoded.length > 0 ? `?${encoded}` : "";
}

export function parsePlatformActionTarget(value: unknown): PlatformActionTarget | null {
  const parsed = platformActionTargetSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }

  if (
    parsed.data.kind === "offer_checkout"
    && !parsed.data.offerId
    && !parsed.data.planKey
    && !parsed.data.checkoutUrl
  ) {
    return null;
  }

  return parsed.data;
}

export function platformActionTargetFromLegacyUrl(value: unknown): PlatformActionTarget | null {
  if (typeof value !== "string" || value.trim().length === 0) {
    return null;
  }

  return {
    kind: "url",
    href: value,
  };
}

export function resolvePlatformActionTarget(
  action: PlatformActionTarget | null,
): ResolvedPlatformActionTarget {
  if (!action) {
    return {
      href: null,
      openInNewTab: false,
      isInteractive: false,
      reason: "No platform action target is configured for this block.",
      attrs: {},
    };
  }

  if (action.kind === "url") {
    return {
      href: action.href,
      openInNewTab: action.openInNewTab === true,
      isInteractive: true,
      reason: null,
      attrs: {
        "data-ease-action-kind": action.kind,
      },
    };
  }

  if (action.kind === "affiliate_referral") {
    return {
      href: `/api/affiliate/track${toQueryString({
        ref: action.referralCode,
        utm_source: action.utmSource,
        utm_medium: action.utmMedium,
        utm_campaign: action.campaignName,
      })}`,
      openInNewTab: false,
      isInteractive: true,
      reason: null,
      attrs: {
        "data-ease-action-kind": action.kind,
        "data-ease-referral-code": action.referralCode,
      },
    };
  }

  if (action.kind === "webinar_registration") {
    if (action.registrationPageUrl) {
      return {
        href: action.registrationPageUrl,
        openInNewTab: false,
        isInteractive: true,
        reason: null,
        attrs: {
          "data-ease-action-kind": action.kind,
          "data-ease-webinar-id": action.webinarId,
          "data-ease-registration-mode": action.registrationMode,
        },
      };
    }

    return {
      href: null,
      openInNewTab: false,
      isInteractive: false,
      reason: "Webinar actions need an explicit hosted registration URL before they can render as a public CTA.",
      attrs: {
        "data-ease-action-kind": action.kind,
        "data-ease-webinar-id": action.webinarId,
        "data-ease-registration-mode": action.registrationMode,
        "data-ease-expected-route": "/v1/webinars/:webinarId/registrations",
      },
    };
  }

  if (action.kind === "offer_checkout" && action.checkoutUrl) {
    return {
      href: action.checkoutUrl,
      openInNewTab: false,
      isInteractive: true,
      reason: null,
      attrs: {
        "data-ease-action-kind": action.kind,
        ...(action.offerId ? { "data-ease-offer-id": action.offerId } : {}),
        ...(action.planKey ? { "data-ease-plan-key": action.planKey } : {}),
      },
    };
  }

  if (action.kind === "offer_checkout") {
    return {
      href: null,
      openInNewTab: false,
      isInteractive: false,
      reason: "Offer checkout requires a hosted checkout URL or a dedicated authenticated runtime before it can be rendered as a public CTA.",
      attrs: {
        "data-ease-action-kind": action.kind,
        ...(action.offerId ? { "data-ease-offer-id": action.offerId } : {}),
        ...(action.planKey ? { "data-ease-plan-key": action.planKey } : {}),
        "data-ease-expected-route": "/v1/billing/checkout",
        "data-ease-checkout-mode": action.checkoutMode,
      },
    };
  }

  return {
    href: null,
    openInNewTab: false,
    isInteractive: false,
    reason: "No platform action target is configured for this block.",
    attrs: {},
  };
}

export function buildHtmlDataAttributes(attrs: Record<string, string>): string {
  return Object.entries(attrs)
    .map(([key, value]) => ` ${key}="${escapeHtmlAttribute(value)}"`)
    .join("");
}

export function resolveOrderBumpBinding(input: {
  productId: string;
  binding: unknown;
}): OrderBumpBinding {
  const parsed = orderBumpBindingSchema.safeParse(input.binding);
  if (parsed.success) {
    return {
      ...parsed.data,
      fieldName: parsed.data.fieldName ?? `_order_bump_${input.productId}`,
    };
  }

  return {
    kind: "funnel_submission",
    fieldName: `_order_bump_${input.productId}`,
    selectedValue: "1",
  };
}

export function parseWebinarPageConfig(value: unknown): WebinarPageConfig | null {
  const parsed = webinarPageConfigSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
